import { FeatureCalendar } from '@mdlbeasts/nofomo.ui.web';
import { Box, Container, Divider, IconButton, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DateFormat, i18nFormat } from 'utils/date.utils';
import { replacePathParams } from 'utils/routes.utils';

import {
  CALENDAR_DAY_PARAM,
  CALENDAR_MONTHS_INITIAL,
} from 'constants/calendar.constants';
import { EVENT_DAY_HOUR_OFFSET } from 'constants/i18n.constants';
import { Routes } from 'constants/routes.constants';
import { useLocale } from 'hooks/useLocale';
import { useOverlayRoute } from 'hooks/useOverlayRoute';
import { useCalendarMonthEvents } from 'queries/events/useCalendarMonthEvents';

import EventDayDialog from 'components/@calendar/EventDayDialog';
import {
  toCalendarEvents,
  toWeekdayLabels,
} from 'components/@calendar/normalizers';
import {
  buildMonths,
  monthsUntil,
  parseDayKey,
} from 'components/@calendar/utils';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import Icon from 'components/@icons/Icon';
import PageBreadcrumbs from 'components/@navigation/PageBreadcrumbs';

interface Props {
  // "yyyy-MM" of the month to open on, resolved on the server.
  anchorMonth: string;
}

const CalendarContainer = ({ anchorMonth }: Props) => {
  const intl = useIntl();
  const router = useRouter();
  const { locale } = useLocale();

  const { open, replace, close } = useOverlayRoute({
    param: CALENDAR_DAY_PARAM,
  });

  const [monthCount, setMonthCount] = useState(CALENDAR_MONTHS_INITIAL);

  // Memoised: a fresh Date each render would rebuild every list below it.
  const anchor = useMemo(
    () => parseDayKey(`${anchorMonth}-01`) ?? new Date(),
    [anchorMonth],
  );

  // A hand-edited or stale param must render as closed, never throw.
  const selected = useMemo(() => {
    const param = router.query[CALENDAR_DAY_PARAM];
    const key = typeof param === 'string' ? param : null;
    const date = key ? parseDayKey(key) : null;

    return { key: date ? key : null, date };
  }, [router.query]);

  const months = useMemo(() => {
    // A deep link into a month past the window still needs that month fetched.
    const required = selected.date ? monthsUntil(anchor, selected.date) + 1 : 0;

    return buildMonths(anchor, Math.max(monthCount, required), locale);
  }, [anchor, monthCount, selected.date, locale]);

  const { events } = useCalendarMonthEvents({ months });

  const calendarEvents = useMemo(
    () => toCalendarEvents({ events, intl }),
    [events, intl],
  );

  const weekdays = useMemo(() => toWeekdayLabels(locale), [locale]);

  // Earlier months are hidden, so the sheet cannot step before the first one.
  const minDayKey = `${anchorMonth}-01`;

  return (
    <>
      <PageBreadcrumbs
        hideOnMobile
        items={[{ label: <FormattedMessage id="events.calendar.title" /> }]}
      />

      {/* 76px below the breadcrumb on desktop, per the design. */}
      <Container sx={{ mt: { xs: 2, md: 9.5 }, pb: 5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            position: 'relative',
            // 45px to the first month, per the design.
            mb: { xs: 3, md: '45px' },
          }}
        >
          {/* heading 06 on mobile, heading 05 on desktop, per the design. */}
          <Typography
            component="h1"
            sx={{
              typography: { xs: 'h6', md: 'h5' },
              color: { xs: 'text.primary', md: 'common.white' },
            }}
          >
            <FormattedMessage id="events.calendar.title" />
          </Typography>

          {/* Mobile is designed as a dismissible screen rather than a page. */}
          <IconButton
            size="small"
            variant="filled"
            component={LinkUnstyled}
            href={Routes.Home}
            aria-label={intl.formatMessage({ id: 'navigation.close' })}
            sx={{
              display: { xs: 'flex', md: 'none' },
              position: 'absolute',
              insetInlineEnd: 0,
            }}
          >
            <Icon name="Close" fontSize={24} />
          </IconButton>
        </Box>

        <FeatureCalendar.MonthList
          months={months}
          weekdays={weekdays}
          events={calendarEvents}
          dayHourOffset={EVENT_DAY_HOUR_OFFSET}
          actions={{
            pressDay: open,
            next: () =>
              setMonthCount((count) => count + CALENDAR_MONTHS_INITIAL),
          }}
          testID="calendar-months"
        />

        {/* Closes the page off from the footer, as the lineup does. */}
        <Divider sx={{ borderColor: 'grey.800', mt: 5 }} />
      </Container>

      <EventDayDialog
        isOpen={!!selected.key}
        dayKey={selected.key ?? ''}
        dateLabel={
          selected.date
            ? i18nFormat(
                selected.date,
                DateFormat.FullMonthDate[locale],
                locale,
              )
            : ''
        }
        eventDate={
          selected.date
            ? i18nFormat(selected.date, DateFormat.MonthDay[locale], locale)
            : ''
        }
        minDayKey={minDayKey}
        events={calendarEvents}
        dayHourOffset={EVENT_DAY_HOUR_OFFSET}
        onClose={close}
        onPressEvent={(slug) =>
          void router.push(replacePathParams(Routes.EventDetail, { slug }))
        }
        onChangeDay={replace}
      />
    </>
  );
};

export default CalendarContainer;
